// MyVouchers.js
import React, { useEffect, useMemo, useState } from 'react';
import {
  SafeAreaView,
  StyleSheet,
  View,
  Text,
  ScrollView,
  Image,
  TouchableOpacity,
  ActivityIndicator,
  RefreshControl,
  Alert,
  Linking,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';

const API_URL = 'https://collabbay.com/APIControllers/FetchCreatorVouchers.php';
const LOGO_URL = 'https://collabbay.com/CollabbayLogoDark.png';
const FALLBACK_IMAGE =
  'https://images.pexels.com/photos/1058277/pexels-photo-1058277.jpeg';

const CREATOR_EARNINGS_PER_REDEMPTION = 5;

const normaliseImageUri = (image) => {
  if (!image || !String(image).trim()) return FALLBACK_IMAGE;

  const trimmed = String(image).trim();

  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return trimmed;
  }

  if (trimmed.startsWith('/')) {
    return `https://collabbay.com${trimmed}`;
  }

  return `https://collabbay.com/${trimmed}`;
};

const formatNumber = (value) => {
  const n = Number(value || 0);
  return Number.isFinite(n) ? n.toLocaleString('en-GB') : '0';
};

const formatMoney = (value) => {
  const n = Number(value || 0);
  return `£${Number.isFinite(n) ? n.toLocaleString('en-GB') : '0'}`;
};

const formatDate = (value) => {
  if (!value) return 'Recently';

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const formatDealText = (voucher) => {
  const spend = Number(voucher.OnSpend || 0);
  const discount = Number(voucher.Discount || 0);

  if (spend > 0 && discount > 0) {
    return `When customers spend £${spend}, they get ${discount}% off`;
  }

  if (discount > 0) {
    return `${discount}% off for your audience`;
  }

  return 'Creator voucher deal';
};

export default function MyVouchers({ navigation }) {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [userData, setUserData] = useState({});
  const [vouchers, setVouchers] = useState([]);
  const [ledger, setLedger] = useState([]);
  const [summary, setSummary] = useState({
    TotalDeals: 0,
    TotalStockAssigned: 0,
    TotalRedeemed: 0,
    TotalRemaining: 0,
    EstimatedCreatorEarnings: 0,
  });

  const [activeTab, setActiveTab] = useState('stock');
  const [errorMessage, setErrorMessage] = useState('');

  const fetchUserData = async () => {
    try {
      const response = await fetch(
        'https://collabbay.com/APIControllers/GetSessionData.php',
        {
          method: 'GET',
          credentials: 'include',
        }
      );

      const data = await response.json();
      setUserData(data || {});
    } catch (error) {
      console.error('Fetch user data error:', error);
    }
  };

  const fetchCreatorVouchers = async () => {
    const response = await fetch(`${API_URL}?d=${Date.now()}`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
      },
    });

    const rawText = await response.text();

    let data;

    try {
      data = JSON.parse(rawText);
    } catch (error) {
      throw new Error(`Invalid voucher response: ${rawText.slice(0, 160)}`);
    }

    if (!response.ok || !data.success) {
      throw new Error(data.message || data.debug || 'Could not load vouchers.');
    }

    setVouchers(Array.isArray(data.vouchers) ? data.vouchers : []);
    setLedger(Array.isArray(data.ledger) ? data.ledger : []);
    setSummary(
      data.summary || {
        TotalDeals: 0,
        TotalStockAssigned: 0,
        TotalRedeemed: 0,
        TotalRemaining: 0,
        EstimatedCreatorEarnings: 0,
      }
    );
    setErrorMessage('');
  };

  const loadPage = async () => {
    try {
      await Promise.all([fetchUserData(), fetchCreatorVouchers()]);
    } catch (error) {
      console.error('Load creator vouchers error:', error);
      setErrorMessage(error.message || 'Could not load your vouchers.');
    }
  };

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      await loadPage();
      setLoading(false);
    };

    run();
  }, []);

  const onRefresh = async () => {
    setRefreshing(true);
    await loadPage();
    setRefreshing(false);
  };

  const totalEarnings = useMemo(() => {
    return Number(summary.TotalRedeemed || 0) * CREATOR_EARNINGS_PER_REDEMPTION;
  }, [summary.TotalRedeemed]);

  const liveVouchers = useMemo(() => {
    return vouchers.filter((item) => Number(item.Remaining || 0) > 0);
  }, [vouchers]);

  const redeemedVouchers = useMemo(() => {
    return vouchers.filter((item) => Number(item.Redeemed || 0) > 0);
  }, [vouchers]);

  const handleFooterPress = (routeName, fallbackUrl = null) => {
    if (routeName && navigation?.navigate) {
      navigation.navigate(routeName);
      return;
    }

    if (fallbackUrl) {
      Linking.openURL(fallbackUrl);
    }
  };

  const renderHeader = () => (
    <View style={styles.header}>
      <View style={styles.headerRow}>
        <Image source={{ uri: LOGO_URL }} style={styles.logo} />

        <View style={{ flex: 1 }}>
          <Text style={styles.headerTitle}>My Vouchers</Text>
          <Text style={styles.headerSubtitle}>
            {userData?.Username
              ? `Hi 👋, ${userData.Username}`
              : 'Creator voucher dashboard'}
          </Text>
        </View>

        <TouchableOpacity
          style={styles.helpButton}
          onPress={() =>
            Alert.alert(
              'Creator vouchers',
              'Brands can assign voucher batches to you after a collab. Share them with your audience, track redemptions, and see your estimated sales value. For now, each redemption is assumed to be worth £5 to you.'
            )
          }
        >
          <Ionicons name="help-circle-outline" size={22} color="#111" />
        </TouchableOpacity>
      </View>
    </View>
  );

  const renderHero = () => (
    <View style={styles.hero}>
      <LinearGradient
        colors={['#111', '#000']}
        style={styles.heroInner}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
      >
        <Text style={styles.heroBadge}>CREATOR VOUCHERS</Text>
        <Text style={styles.heroTitle}>Turn collabs into sales.</Text>
        <Text style={styles.heroCopy}>
          View the vouchers brands have given you, see what your audience has
          redeemed, and track estimated earnings from your voucher activity.
        </Text>

        <View style={styles.heroStatsRow}>
          <View style={styles.heroStat}>
            <Text style={styles.heroStatValue}>
              {formatNumber(summary.TotalRemaining)}
            </Text>
            <Text style={styles.heroStatLabel}>Live stock</Text>
          </View>

          <View style={styles.heroStat}>
            <Text style={styles.heroStatValue}>
              {formatNumber(summary.TotalRedeemed)}
            </Text>
            <Text style={styles.heroStatLabel}>Redeemed</Text>
          </View>

          <View style={styles.heroStat}>
            <Text style={styles.heroStatValue}>{formatMoney(totalEarnings)}</Text>
            <Text style={styles.heroStatLabel}>Est. sales</Text>
          </View>
        </View>
      </LinearGradient>
    </View>
  );

  const renderSummaryCards = () => (
    <View style={styles.summaryGrid}>
      <View style={styles.summaryCard}>
        <View style={styles.summaryIcon}>
          <Ionicons name="ticket-outline" size={18} color="#111" />
        </View>
        <Text style={styles.summaryValue}>
          {formatNumber(summary.TotalStockAssigned)}
        </Text>
        <Text style={styles.summaryLabel}>Total vouchers</Text>
      </View>

      <View style={styles.summaryCard}>
        <View style={styles.summaryIcon}>
          <Ionicons name="storefront-outline" size={18} color="#111" />
        </View>
        <Text style={styles.summaryValue}>
          {formatNumber(summary.TotalDeals)}
        </Text>
        <Text style={styles.summaryLabel}>Brand deals</Text>
      </View>

      <View style={styles.summaryCard}>
        <View style={styles.summaryIcon}>
          <Ionicons name="cash-outline" size={18} color="#111" />
        </View>
        <Text style={styles.summaryValue}>{formatMoney(totalEarnings)}</Text>
        <Text style={styles.summaryLabel}>£5 per redeem</Text>
      </View>
    </View>
  );

  const renderTabs = () => (
    <View style={styles.tabs}>
      <TouchableOpacity
        style={[styles.tab, activeTab === 'stock' && styles.tabActive]}
        onPress={() => setActiveTab('stock')}
      >
        <Text style={[styles.tabText, activeTab === 'stock' && styles.tabTextActive]}>
          Live stock
        </Text>
      </TouchableOpacity>

      <TouchableOpacity
        style={[styles.tab, activeTab === 'redemptions' && styles.tabActive]}
        onPress={() => setActiveTab('redemptions')}
      >
        <Text
          style={[
            styles.tabText,
            activeTab === 'redemptions' && styles.tabTextActive,
          ]}
        >
          Redemptions
        </Text>
      </TouchableOpacity>

      <TouchableOpacity
        style={[styles.tab, activeTab === 'ledger' && styles.tabActive]}
        onPress={() => setActiveTab('ledger')}
      >
        <Text style={[styles.tabText, activeTab === 'ledger' && styles.tabTextActive]}>
          Ledger
        </Text>
      </TouchableOpacity>
    </View>
  );

  const renderVoucherCard = (voucher) => {
    const brandImage = normaliseImageUri(voucher.BrandProfileImage);
    const redeemed = Number(voucher.Redeemed || 0);
    const remaining = Number(voucher.Remaining || 0);
    const stock = Number(voucher.StockAssigned || 0);
    const earnings = redeemed * CREATOR_EARNINGS_PER_REDEMPTION;

    return (
      <TouchableOpacity
        key={`${voucher.CollabID}-${voucher.BrandName}-${voucher.OnSpend}-${voucher.Discount}`}
        style={styles.voucherCard}
        activeOpacity={0.92}
      >
        <View style={styles.voucherTop}>
          <Image source={{ uri: brandImage }} style={styles.brandImage} />

          <View style={{ flex: 1 }}>
            <Text style={styles.brandName} numberOfLines={1}>
              {voucher.DisplayBrandName || voucher.BrandName || 'Brand'}
            </Text>
            <Text style={styles.voucherDealText} numberOfLines={2}>
              {formatDealText(voucher)}
            </Text>
          </View>

          <View style={styles.statusPill}>
            <Text style={styles.statusPillText}>
              {remaining > 0 ? 'Live' : 'Used'}
            </Text>
          </View>
        </View>

        <View style={styles.voucherMetricRow}>
          <View style={styles.voucherMetric}>
            <Text style={styles.voucherMetricValue}>{formatNumber(stock)}</Text>
            <Text style={styles.voucherMetricLabel}>Given</Text>
          </View>

          <View style={styles.voucherMetric}>
            <Text style={styles.voucherMetricValue}>{formatNumber(remaining)}</Text>
            <Text style={styles.voucherMetricLabel}>Left</Text>
          </View>

          <View style={styles.voucherMetric}>
            <Text style={styles.voucherMetricValue}>{formatNumber(redeemed)}</Text>
            <Text style={styles.voucherMetricLabel}>Redeemed</Text>
          </View>
        </View>

        <View style={styles.earningsStrip}>
          <View>
            <Text style={styles.earningsLabel}>Estimated sales value</Text>
            <Text style={styles.earningsText}>
              {formatMoney(earnings)} from {formatNumber(redeemed)} redemption
              {redeemed === 1 ? '' : 's'}
            </Text>
          </View>

          <View style={styles.earningsBadge}>
            <Text style={styles.earningsBadgeText}>£5 each</Text>
          </View>
        </View>
      </TouchableOpacity>
    );
  };

  const renderEmptyState = (title, copy) => (
    <View style={styles.emptyCard}>
      <Ionicons name="ticket-outline" size={34} color="#111" />
      <Text style={styles.emptyTitle}>{title}</Text>
      <Text style={styles.emptyCopy}>{copy}</Text>
    </View>
  );

  const renderStockTab = () => {
    if (liveVouchers.length === 0) {
      return renderEmptyState(
        'No live voucher stock yet',
        'When brands assign vouchers to you, they will appear here.'
      );
    }

    return <View style={styles.cardList}>{liveVouchers.map(renderVoucherCard)}</View>;
  };

  const renderRedemptionsTab = () => {
    if (redeemedVouchers.length === 0) {
      return renderEmptyState(
        'No redemptions yet',
        'Once customers redeem your vouchers, sales activity will show here.'
      );
    }

    return (
      <View style={styles.cardList}>
        {redeemedVouchers.map((voucher) => renderVoucherCard(voucher))}
      </View>
    );
  };

  const renderLedgerRow = (item, index) => {
    const brandImage = normaliseImageUri(item.BrandProfileImage);
    const brandName = item.DisplayBrandName || item.BrandName || 'Brand';
    const redeemDate = item.RedeemedAt || item.CreatedAt || item.DateCreated || item.Date;

    return (
      <View key={`${item.CollabID}-${index}`} style={styles.ledgerRow}>
        <Image source={{ uri: brandImage }} style={styles.ledgerImage} />

        <View style={{ flex: 1 }}>
          <Text style={styles.ledgerBrand} numberOfLines={1}>
            {brandName}
          </Text>
          <Text style={styles.ledgerMeta}>
            {formatDate(redeemDate)}
            {item.VoucherCode ? ` • ${item.VoucherCode}` : ''}
          </Text>
        </View>

        <View style={styles.ledgerAmount}>
          <Text style={styles.ledgerAmountText}>+£5</Text>
          <Text style={styles.ledgerAmountSub}>sale</Text>
        </View>
      </View>
    );
  };

  const renderLedgerTab = () => {
    if (ledger.length === 0) {
      return renderEmptyState(
        'Ledger is empty',
        'Every voucher redemption will be treated as a sale and listed here.'
      );
    }

    return (
      <View style={styles.ledgerCard}>
        <View style={styles.ledgerHeader}>
          <Text style={styles.ledgerTitle}>Redemption ledger</Text>
          <Text style={styles.ledgerSub}>
            Every redemption is currently counted as £5.
          </Text>
        </View>

        {ledger.map(renderLedgerRow)}
      </View>
    );
  };

  const renderActiveTab = () => {
    if (activeTab === 'stock') return renderStockTab();
    if (activeTab === 'redemptions') return renderRedemptionsTab();
    return renderLedgerTab();
  };

  if (loading) {
    return (
      <View style={styles.loadingPage}>
        <ActivityIndicator size="large" color="#111" />
        <Text style={styles.loadingText}>Loading your vouchers...</Text>
      </View>
    );
  }

  return (
    <View style={styles.outerPage}>
      <View style={styles.yellowStrip} />

      <SafeAreaView style={styles.container}>
        <LinearGradient
          colors={['#FDF05D', 'rgba(253,240,93,0)']}
          start={{ x: 0, y: 0 }}
          end={{ x: 0, y: 1 }}
          style={styles.topGlow}
          pointerEvents="none"
        />

        <ScrollView
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
          }
        >
          {renderHeader()}

          {errorMessage ? (
            <View style={styles.errorBox}>
              <Text style={styles.errorTitle}>Could not load vouchers</Text>
              <Text style={styles.errorText}>{errorMessage}</Text>
            </View>
          ) : null}

          {renderHero()}
          {renderSummaryCards()}
          {renderTabs()}
          {renderActiveTab()}

          <TouchableOpacity
            style={styles.privacyLink}
            onPress={() =>
              Linking.openURL('https://collabbay.com/PrivacyPolicy.html')
            }
          >
            <Text style={styles.privacyText}>
              By using collabbay, you agree to our Privacy Policy.
            </Text>
          </TouchableOpacity>
        </ScrollView>

        <View style={styles.footer}>
          <TouchableOpacity
            style={styles.footerIcon}
            onPress={() => handleFooterPress('Homepage')}
          >
            <Ionicons name="home" size={24} color="black" />
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.footerIcon}
            onPress={() => handleFooterPress('CollabCalendar')}
          >
            <Ionicons name="calendar" size={24} color="black" />
          </TouchableOpacity>

          <TouchableOpacity
            onPress={() => handleFooterPress('MyFeed')}
            style={styles.footerMainButton}
          >
            <Ionicons name="film-outline" size={22} color="black" />
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.footerIcon}
            onPress={() => handleFooterPress('UserActivityFeed')}
          >
            <Ionicons name="chatbox-ellipses-outline" size={24} color="black" />
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.footerIcon}
            onPress={() => handleFooterPress('UserRecapPage')}
          >
            <Ionicons name="person" size={24} color="black" />
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    </View>
  );
}

const styles = StyleSheet.create({
  outerPage: {
    flex: 1,
    backgroundColor: '#fff',
    alignItems: 'center',
  },
  yellowStrip: {
    height: 12,
    width: '100%',
    maxWidth: 600,
    backgroundColor: '#FDF05D',
  },
  container: {
    flex: 1,
    width: '100%',
    maxWidth: 600,
    backgroundColor: '#fff',
  },
  topGlow: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 220,
    zIndex: 0,
  },
  scrollContent: {
    paddingHorizontal: 14,
    paddingBottom: 110,
  },
  loadingPage: {
    flex: 1,
    backgroundColor: '#fff',
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadingText: {
    marginTop: 10,
    color: '#555',
    fontSize: 13,
    fontWeight: '700',
  },
  header: {
    width: '100%',
    paddingTop: 20,
    paddingBottom: 12,
    zIndex: 2,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  logo: {
    width: 42,
    height: 42,
    borderRadius: 21,
    backgroundColor: '#000',
    marginRight: 10,
  },
  headerTitle: {
    fontSize: 24,
    fontWeight: '900',
    color: '#111',
    letterSpacing: -0.5,
  },
  headerSubtitle: {
    marginTop: 1,
    fontSize: 13,
    color: '#666',
    fontWeight: '700',
  },
  helpButton: {
    width: 38,
    height: 38,
    borderRadius: 19,
    backgroundColor: '#f3f3f3',
    alignItems: 'center',
    justifyContent: 'center',
  },
  errorBox: {
    backgroundColor: '#fff4f4',
    borderWidth: 1,
    borderColor: '#ffd0d0',
    borderRadius: 18,
    padding: 14,
    marginBottom: 14,
  },
  errorTitle: {
    color: '#9f1d1d',
    fontWeight: '900',
    fontSize: 14,
  },
  errorText: {
    color: '#9f1d1d',
    fontSize: 12,
    marginTop: 4,
    lineHeight: 17,
  },
  hero: {
    marginTop: 6,
    marginBottom: 14,
  },
  heroInner: {
    borderRadius: 30,
    padding: 22,
    overflow: 'hidden',
  },
  heroBadge: {
    color: '#FDF05D',
    fontSize: 11,
    fontWeight: '900',
    letterSpacing: 1.4,
    marginBottom: 10,
  },
  heroTitle: {
    color: '#fff',
    fontSize: 30,
    lineHeight: 34,
    fontWeight: '900',
    letterSpacing: -1,
  },
  heroCopy: {
    color: '#d8d8d8',
    marginTop: 10,
    fontSize: 14,
    lineHeight: 20,
    fontWeight: '600',
  },
  heroStatsRow: {
    flexDirection: 'row',
    gap: 9,
    marginTop: 18,
  },
  heroStat: {
    flex: 1,
    backgroundColor: 'rgba(255,255,255,0.08)',
    borderRadius: 18,
    padding: 12,
  },
  heroStatValue: {
    color: '#fff',
    fontSize: 20,
    fontWeight: '900',
  },
  heroStatLabel: {
    color: '#d8d8d8',
    marginTop: 4,
    fontSize: 11,
    fontWeight: '700',
  },
  summaryGrid: {
    flexDirection: 'row',
    gap: 10,
    marginBottom: 14,
  },
  summaryCard: {
    flex: 1,
    backgroundColor: '#f7f7f7',
    borderRadius: 22,
    padding: 13,
    borderWidth: 1,
    borderColor: '#eeeeee',
  },
  summaryIcon: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#FDF05D',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 9,
  },
  summaryValue: {
    fontSize: 18,
    fontWeight: '900',
    color: '#111',
  },
  summaryLabel: {
    marginTop: 3,
    fontSize: 10,
    color: '#666',
    fontWeight: '800',
  },
  tabs: {
    flexDirection: 'row',
    backgroundColor: '#f3f3f3',
    borderRadius: 999,
    padding: 5,
    marginBottom: 14,
  },
  tab: {
    flex: 1,
    paddingVertical: 10,
    borderRadius: 999,
    alignItems: 'center',
  },
  tabActive: {
    backgroundColor: '#111',
  },
  tabText: {
    color: '#555',
    fontSize: 12,
    fontWeight: '900',
  },
  tabTextActive: {
    color: '#fff',
  },
  cardList: {
    gap: 14,
  },
  voucherCard: {
    backgroundColor: '#fff',
    borderRadius: 26,
    padding: 15,
    borderWidth: 1,
    borderColor: '#eeeeee',
    shadowColor: '#000',
    shadowOpacity: 0.07,
    shadowRadius: 14,
    shadowOffset: { width: 0, height: 6 },
    elevation: 3,
  },
  voucherTop: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  brandImage: {
    width: 58,
    height: 58,
    borderRadius: 18,
    backgroundColor: '#111',
    marginRight: 12,
  },
  brandName: {
    fontSize: 18,
    fontWeight: '900',
    color: '#111',
  },
  voucherDealText: {
    marginTop: 4,
    fontSize: 13,
    color: '#666',
    fontWeight: '700',
    lineHeight: 18,
  },
  statusPill: {
    backgroundColor: '#FDF05D',
    borderRadius: 999,
    paddingVertical: 6,
    paddingHorizontal: 10,
    marginLeft: 8,
  },
  statusPillText: {
    color: '#111',
    fontSize: 10,
    fontWeight: '900',
  },
  voucherMetricRow: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 15,
  },
  voucherMetric: {
    flex: 1,
    backgroundColor: '#fafafa',
    borderRadius: 18,
    padding: 12,
  },
  voucherMetricValue: {
    color: '#111',
    fontSize: 18,
    fontWeight: '900',
  },
  voucherMetricLabel: {
    color: '#777',
    fontSize: 10,
    fontWeight: '800',
    marginTop: 3,
  },
  earningsStrip: {
    marginTop: 14,
    backgroundColor: '#111',
    borderRadius: 20,
    padding: 14,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  earningsLabel: {
    color: '#FDF05D',
    fontSize: 10,
    fontWeight: '900',
    letterSpacing: 1,
    textTransform: 'uppercase',
  },
  earningsText: {
    color: '#fff',
    marginTop: 5,
    fontSize: 14,
    fontWeight: '800',
  },
  earningsBadge: {
    backgroundColor: '#FDF05D',
    borderRadius: 999,
    paddingVertical: 7,
    paddingHorizontal: 10,
    marginLeft: 8,
  },
  earningsBadgeText: {
    color: '#111',
    fontSize: 11,
    fontWeight: '900',
  },
  emptyCard: {
    backgroundColor: '#f7f7f7',
    borderRadius: 26,
    padding: 28,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#eeeeee',
  },
  emptyTitle: {
    marginTop: 10,
    fontSize: 17,
    fontWeight: '900',
    color: '#111',
    textAlign: 'center',
  },
  emptyCopy: {
    marginTop: 5,
    fontSize: 13,
    color: '#666',
    lineHeight: 19,
    textAlign: 'center',
    fontWeight: '600',
  },
  ledgerCard: {
    backgroundColor: '#fff',
    borderRadius: 26,
    padding: 15,
    borderWidth: 1,
    borderColor: '#eeeeee',
  },
  ledgerHeader: {
    marginBottom: 10,
  },
  ledgerTitle: {
    fontSize: 18,
    fontWeight: '900',
    color: '#111',
  },
  ledgerSub: {
    marginTop: 4,
    fontSize: 12,
    color: '#666',
    fontWeight: '700',
  },
  ledgerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    borderTopWidth: 1,
    borderTopColor: '#f0f0f0',
  },
  ledgerImage: {
    width: 42,
    height: 42,
    borderRadius: 14,
    backgroundColor: '#111',
    marginRight: 10,
  },
  ledgerBrand: {
    fontSize: 14,
    fontWeight: '900',
    color: '#111',
  },
  ledgerMeta: {
    marginTop: 3,
    fontSize: 12,
    color: '#777',
    fontWeight: '700',
  },
  ledgerAmount: {
    alignItems: 'flex-end',
  },
  ledgerAmountText: {
    color: '#0b7a32',
    fontSize: 16,
    fontWeight: '900',
  },
  ledgerAmountSub: {
    marginTop: 2,
    color: '#777',
    fontSize: 10,
    fontWeight: '800',
  },
  privacyLink: {
    marginTop: 24,
    marginBottom: 8,
  },
  privacyText: {
    fontSize: 11,
    color: 'black',
    textDecorationLine: 'underline',
    textAlign: 'center',
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    position: 'sticky',
    bottom: 0,
    backgroundColor: 'white',
    left: 0,
    right: 0,
    paddingVertical: 10,
    borderTopWidth: 1,
    borderTopColor: '#f0f0f0',
  },
  footerIcon: {
    alignItems: 'center',
    justifyContent: 'center',
    width: 42,
    height: 42,
  },
  footerMainButton: {
    backgroundColor: '#FDF05D',
    padding: 10,
    borderRadius: 20,
  },
});